import { EFFORT_LEVELS } from "@switchboard/shared";
import { useState } from "react";
import { errorText, post, useApi } from "../lib/api.ts";
import { EFFORT_HELP, MODEL_CHOICES } from "../lib/format.ts";
import type { EnvFile, FolderListing } from "../lib/types.ts";

const KNOWN_MODELS = new Set(MODEL_CHOICES.flatMap((g) => g.models.map((m) => m.value)));
const CUSTOM = "__custom__";

/** A model from the list, or any model ID typed in. "" means inherit (`inheritLabel` says from where). */
export function ModelPicker({ value, onChange, inheritLabel = "Claude Code's default" }: { value: string; onChange: (v: string) => void; inheritLabel?: string }) {
  const [custom, setCustom] = useState(() => Boolean(value) && !KNOWN_MODELS.has(value));
  const showInput = custom || (Boolean(value) && !KNOWN_MODELS.has(value));
  return (
    <div className="stack tight">
      <select
        value={showInput ? CUSTOM : value}
        onChange={(e) => {
          const v = e.target.value;
          setCustom(v === CUSTOM);
          onChange(v === CUSTOM ? "" : v);
        }}
      >
        <option value="">{inheritLabel}</option>
        {MODEL_CHOICES.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.models.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label} · {m.value}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={CUSTOM}>Other model ID…</option>
      </select>
      {showInput && <input className="mono" autoFocus value={value} placeholder="claude-opus-5-5" onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

export function EffortSelect({ value, onChange, inheritLabel = "Model default" }: { value: string; onChange: (v: string) => void; inheritLabel?: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{inheritLabel}</option>
      {EFFORT_LEVELS.map((l) => (
        <option key={l} value={l}>
          {l} · {EFFORT_HELP[l]}
        </option>
      ))}
    </select>
  );
}

const folderUrl = (p: string) => `/api/folders?path=${encodeURIComponent(p)}`;

/**
 * A folder under the projects root: type it, or browse and create folders on the server.
 * `value` is relative to the root.
 */
export function FolderPicker({ label, value, onChange, placeholder, hint }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="field">
      <span>{label}</span>
      <div className="row nowrap">
        <input className="mono" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="btn" onClick={() => setOpen((o) => !o)}>
          {open ? "Close" : "Browse…"}
        </button>
      </div>
      {hint && <span className="hint">{hint}</span>}
      {open && (
        <FolderBrowser
          onPick={(p) => {
            onChange(p);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function FolderBrowser({ onPick }: { onPick: (path: string) => void }) {
  const [at, setAt] = useState("");
  const listing = useApi<FolderListing>(folderUrl(at), { revalidateOnFocus: false });
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const l = listing.data;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const made = await post<FolderListing>("/api/folders", { path: at ? `${at}/${newName.trim()}` : newName.trim() });
      setNewName("");
      await listing.mutate();
      onPick(made.path);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 12, gap: 8 }}>
      <div className="row between">
        <span className="mono small ellipsis" title={l?.abs}>
          {l ? l.abs : "…"}
        </span>
        <span className="row nowrap">
          <button type="button" className="btn sm" disabled={l?.parent === null || !l} onClick={() => l && setAt(l.parent ?? "")}>
            ↑ Up
          </button>
          <button type="button" className="btn sm primary" disabled={!l?.path} onClick={() => l && onPick(l.path)}>
            Use this folder
          </button>
        </span>
      </div>
      {listing.error ? (
        <div className="callout bad">{errorText(listing.error)}</div>
      ) : !l ? (
        <div className="muted small">Loading…</div>
      ) : l.folders.length ? (
        <div className="stack tight" style={{ maxHeight: 220, overflowY: "auto" }}>
          {l.folders.map((f) => (
            <div key={f.name} className="row between nowrap small">
              <button type="button" className="btn ghost sm mono" onClick={() => setAt(l.path ? `${l.path}/${f.name}` : f.name)}>
                📁 {f.name}
              </button>
              <span className="row nowrap">
                {f.isGit && <span className="pill ok">git</span>}
                <button type="button" className="btn sm" onClick={() => onPick(l.path ? `${l.path}/${f.name}` : f.name)}>
                  Select
                </button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="muted small">No folders here.</div>
      )}
      <div className="row nowrap">
        <input className="mono" value={newName} placeholder="new-folder" onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newName.trim() && (e.preventDefault(), void create())} />
        <button type="button" className="btn" disabled={busy || !newName.trim()} onClick={() => void create()}>
          New folder
        </button>
      </div>
      {error && <div className="callout bad">{error}</div>}
    </div>
  );
}

/** Env files written into every new worktree (and kept out of git). `repos` are the folders a file can go into. */
export function EnvFilesEditor({ files, onChange, repos }: { files: EnvFile[]; onChange: (files: EnvFile[]) => void; repos: string[] }) {
  const set = (i: number, patch: Partial<EnvFile>) => onChange(files.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const nextRepo = repos.find((r) => !files.some((f) => f.repo === r)) ?? "";
  return (
    <div className="field">
      <span>Env files</span>
      <span className="hint">Written into each new session's worktree and never committed (added to the repository's git exclude). Stored encrypted.</span>
      {files.map((f, i) => (
        <div key={i} className="card" style={{ padding: 10, gap: 8 }}>
          <div className="row nowrap">
            {repos.length > 0 && (
              <select value={f.repo} onChange={(e) => set(i, { repo: e.target.value })} style={{ width: "auto" }}>
                <option value="">workspace root</option>
                {repos.map((r) => (
                  <option key={r} value={r}>
                    {r}/
                  </option>
                ))}
              </select>
            )}
            <input className="mono" value={f.path} placeholder=".env" onChange={(e) => set(i, { path: e.target.value })} />
            <button type="button" className="btn ghost sm" aria-label="Remove env file" onClick={() => onChange(files.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
          <textarea className="mono" rows={4} value={f.content} placeholder={"DATABASE_URL=postgres://…\nAPI_KEY=…"} onChange={(e) => set(i, { content: e.target.value })} />
        </div>
      ))}
      <div>
        <button type="button" className="btn sm" onClick={() => onChange([...files, { repo: nextRepo, path: ".env", content: "" }])}>
          + Add env file
        </button>
      </div>
    </div>
  );
}

/** Problems that would make the server refuse a set of env files. */
export function envFilesError(files: EnvFile[]): string | undefined {
  const keys = files.map((f) => `${f.repo}/${f.path.trim()}`);
  if (files.some((f) => !f.path.trim())) return "every env file needs a path";
  if (files.some((f) => f.path.includes(".."))) return "env file paths cannot contain ..";
  if (new Set(keys).size !== keys.length) return "the same env file is listed twice";
  return undefined;
}
